import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Edit,
  Save,
  Loader2,
  FileText,
  CheckCircle2,
  Search,
  ArrowRightLeft,
  AlertCircle,
  AlertTriangle,
  Info,
  Tags,
  Share2,
} from 'lucide-react';
import {
  knowledgeService,
  entityTypeService,
  type ApprovedDocumentDetail,
  type EntityTypeSummary,
  type BulkUpdatePreviewResponse,
} from '@/services/kbService';
import EmbeddingsTab from './EmbeddingsTab';
import KnowledgeGraphTab from './KnowledgeGraphTab';
import EntityRelationshipsTab from './EntityRelationshipsTab';

import { logger } from '@/utils/logger';
interface ApprovedDocumentModalProps {
  documentId: string | null;
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface EntityDetail {
  id?: string;
  entity_id?: string;
  type?: string;
  entity_type?: string;
  value?: string;
  entity_value?: string;
  confidence?: number;
  context?: string;
  manually_corrected?: boolean;
  previous_type?: string;
}

const ApprovedDocumentModal: React.FC<ApprovedDocumentModalProps> = ({
  documentId,
  visible,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<ApprovedDocumentDetail | null>(null);
  const [activeTab, setActiveTab] = useState('entities');
  const [isEditing, setIsEditing] = useState(false);
  const [savingEntity, setSavingEntity] = useState<string | null>(null);

  // Entity types from backend
  const [entityTypes, setEntityTypes] = useState<EntityTypeSummary[]>([]);
  const [typesLoading, setTypesLoading] = useState(false);

  // Bulk edit state
  const [bulkSearchValue, setBulkSearchValue] = useState('');
  const [bulkFromType, setBulkFromType] = useState<string>('');
  const [bulkToType, setBulkToType] = useState<string>('');
  const [bulkPreview, setBulkPreview] = useState<BulkUpdatePreviewResponse | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  useEffect(() => {
    if (visible && documentId) {
      loadDocumentDetail();
      loadEntityTypes();
    } else {
      resetState();
    }
  }, [visible, documentId]);

  const resetState = () => {
    setDetail(null);
    setActiveTab('entities');
    setIsEditing(false);
    setBulkSearchValue('');
    setBulkFromType('');
    setBulkToType('');
    setBulkPreview(null);
  };

  const loadDocumentDetail = async () => {
    if (!documentId) return;

    setLoading(true);
    try {
      const data = await knowledgeService.getDocumentDetail(documentId);
      setDetail(data);
    } catch (error: any) {
      logger.error('Failed to load document detail:', error);
      if (error.response?.status === 404) {
        toast.error('Document not found');
      } else {
        toast.error('Failed to load document details');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadEntityTypes = async () => {
    setTypesLoading(true);
    try {
      const types = await entityTypeService.getEntityTypeSummary(true);
      setEntityTypes(types);
    } catch (error) {
      logger.error('Failed to load entity types:', error);
    } finally {
      setTypesLoading(false);
    }
  };

  const handleEntityTypeChange = async (entityId: string, newType: string) => {
    if (!documentId) return;

    setSavingEntity(entityId);
    try {
      await knowledgeService.updateEntityType(documentId, entityId, newType);
      toast.success(`Entity type updated to ${newType}`);
      await loadDocumentDetail();
      onSuccess();
    } catch (error: any) {
      logger.error('Failed to update entity type:', error);
      toast.error('Failed to update entity type');
    } finally {
      setSavingEntity(null);
    }
  };

  const handleEntityValueChange = async (entityId: string, newValue: string) => {
    if (!documentId) return;

    setSavingEntity(entityId);
    try {
      await knowledgeService.updateEntityValue(documentId, entityId, newValue);
      toast.success('Entity value updated');
      await loadDocumentDetail();
      onSuccess();
    } catch (error: any) {
      logger.error('Failed to update entity value:', error);
      toast.error('Failed to update entity value');
    } finally {
      setSavingEntity(null);
    }
  };

  const handleBulkPreview = async () => {
    if (!bulkSearchValue || !bulkFromType) {
      toast.warning('Please enter entity value and select current type');
      return;
    }

    setBulkLoading(true);
    try {
      const preview = await knowledgeService.previewBulkUpdate({
        entity_value: bulkSearchValue,
        from_type: bulkFromType,
        to_type: bulkToType || bulkFromType,
      });
      setBulkPreview(preview);
    } catch (error: any) {
      logger.error('Failed to preview bulk update:', error);
      toast.error('Failed to preview bulk update');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkUpdate = async () => {
    if (!bulkSearchValue || !bulkFromType || !bulkToType) {
      toast.warning('Please fill all fields');
      return;
    }

    setBulkLoading(true);
    try {
      const result = await knowledgeService.bulkUpdateEntityTypes({
        entity_value: bulkSearchValue,
        from_type: bulkFromType,
        to_type: bulkToType,
      });

      if (result.success) {
        toast.success(
          `Updated ${result.entities_updated} entities in ${result.documents_updated} documents`
        );
        setBulkPreview(null);
        setBulkSearchValue('');
        setBulkFromType('');
        setBulkToType('');
        await loadDocumentDetail();
        onSuccess();
      } else {
        toast.error('Bulk update failed');
      }
    } catch (error: any) {
      logger.error('Failed to perform bulk update:', error);
      toast.error('Failed to perform bulk update');
    } finally {
      setBulkLoading(false);
    }
  };

  const getEntityId = (entity: EntityDetail): string => {
    return entity.id || entity.entity_id || `${entity.value || entity.entity_value}-${entity.type || entity.entity_type}`;
  };

  const getEntityType = (entity: EntityDetail): string => {
    return entity.type || entity.entity_type || 'UNKNOWN';
  };

  const getEntityValue = (entity: EntityDetail): string => {
    return entity.value || entity.entity_value || '';
  };

  const getTypeColor = (type: string): string => {
    const colors: Record<string, string> = {
      PERSON: 'bg-blue-500/15 text-blue-400',
      ORGANIZATION: 'bg-purple-500/15 text-purple-400',
      LOCATION: 'bg-emerald-500/15 text-emerald-400',
      GPE: 'bg-emerald-500/15 text-emerald-400',
      DATE: 'bg-orange-500/15 text-orange-400',
      MONEY: 'bg-amber-500/15 text-amber-400',
      EQUIPMENT: 'bg-cyan-500/15 text-cyan-400',
      CHEMICAL: 'bg-red-500/15 text-red-400',
      SERVICE: 'bg-pink-500/15 text-pink-400',
      MATERIAL: 'bg-amber-500/15 text-amber-400',
      CONTACT: 'bg-indigo-500/15 text-indigo-400',
    };
    return colors[type.toUpperCase()] || 'bg-muted text-muted-foreground';
  };

  const getConfidenceColor = (confidence: number): string => {
    const percent = Math.round(confidence * 100);
    if (percent >= 80) return 'bg-emerald-500/15 text-emerald-400';
    if (percent >= 60) return 'bg-orange-500/15 text-orange-400';
    return 'bg-red-500/15 text-red-400';
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const entities = detail?.extracted_entities || [];

  // Helper to parse nested JSON strings in metadata
  const parseNestedJson = (obj: any): any => {
    if (typeof obj === 'string') {
      try {
        return parseNestedJson(JSON.parse(obj));
      } catch {
        return obj;
      }
    }
    if (Array.isArray(obj)) {
      return obj.map(parseNestedJson);
    }
    if (obj && typeof obj === 'object') {
      const parsed: Record<string, any> = {};
      for (const [key, value] of Object.entries(obj)) {
        parsed[key] = parseNestedJson(value);
      }
      return parsed;
    }
    return obj;
  };

  // Entity type summary
  const entityTypeSummary = useMemo(() => {
    if (!entities.length) return {};
    const summary: Record<string, number> = {};
    entities.forEach((entity) => {
      const type = getEntityType(entity);
      summary[type] = (summary[type] || 0) + 1;
    });
    return summary;
  }, [entities]);

  return (
    <Dialog open={visible} onOpenChange={() => onClose()}>
      <DialogContent className="dark bg-background text-foreground max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Document Details
            {detail && (
              <Badge variant="outline" className="bg-emerald-500/15 text-emerald-400">APPROVED</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="space-y-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Loading document details...</p>
          </div>
        ) : detail ? (
          <>
            {/* Document Info Summary */}
            <Card className="bg-muted/50">
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 py-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">File Name:</span>
                  <span className="text-sm font-medium truncate">{detail.file_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">File Type:</span>
                  <span className="text-xs px-2 py-0.5 border border-border rounded-md bg-background">{detail.file_type.toUpperCase()}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">Size:</span>
                  <span className="text-sm">{formatFileSize(detail.file_size)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">Total Entities:</span>
                  <Badge variant="outline" className="bg-emerald-500/15 text-emerald-400">{entities.length}</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">Approved:</span>
                  <span className="text-sm">
                    {detail.approved_at ? new Date(detail.approved_at).toLocaleString() : 'N/A'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">Tags:</span>
                  <div className="flex flex-wrap gap-1">
                    {detail.suggested_tags?.slice(0, 5).map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Entity Type Summary */}
            {Object.keys(entityTypeSummary).length > 0 && (
              <Card className="bg-muted/30">
                <CardContent className="py-3">
                  <div className="flex flex-wrap gap-2 items-center">
                    <span className="text-sm text-muted-foreground">Entity Types:</span>
                    {Object.entries(entityTypeSummary).map(([type, count]) => (
                      <Badge key={type} variant="outline" className={getTypeColor(type)}>
                        {type}: {count}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full overflow-hidden">
              <TabsList className="grid w-full grid-cols-4 flex-shrink-0">
                <TabsTrigger value="entities" aria-label="Entities" className="flex items-center gap-1.5">
                  <Tags className="h-4 w-4 sm:h-3 sm:w-3" />
                  <span className="hidden sm:inline">Entities ({entities.length})</span>
                </TabsTrigger>
                <TabsTrigger value="bulk" aria-label="Bulk Find & Replace" className="flex items-center gap-1.5">
                  <ArrowRightLeft className="h-4 w-4 sm:h-3 sm:w-3" />
                  <span className="hidden sm:inline">Bulk Find & Replace</span>
                </TabsTrigger>
                <TabsTrigger value="metadata" aria-label="Metadata" className="flex items-center gap-1.5">
                  <Info className="h-4 w-4 sm:h-3 sm:w-3" />
                  <span className="hidden sm:inline">Metadata</span>
                </TabsTrigger>
                <TabsTrigger value="entity-relationships" aria-label="Entity Relationships" className="flex items-center gap-1.5">
                  <Share2 className="h-4 w-4 sm:h-3 sm:w-3" />
                  <span className="hidden sm:inline">Entity Relationships</span>
                </TabsTrigger>
                {/* <TabsTrigger value="embeddings">Embeddings</TabsTrigger>
                <TabsTrigger value="graph">Knowledge Graph</TabsTrigger> */}
              </TabsList>

              {/* Entities Tab */}
              <TabsContent value="entities" className="space-y-4">
                <div className="flex items-center justify-between">
                  <Button
                    size="sm"
                    variant={isEditing ? 'default' : 'outline'}
                    onClick={() => setIsEditing(!isEditing)}
                  >
                    {isEditing ? (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Done Editing
                      </>
                    ) : (
                      <>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit Entity Types
                      </>
                    )}
                  </Button>
                </div>

                {entities.length > 0 ? (
                  <>
                  <div className="sm:hidden space-y-3">
                    {entities.map((entity) => {
                      const entityId = getEntityId(entity);
                      const currentType = getEntityType(entity);
                      const currentValue = getEntityValue(entity);
                      const isSaving = savingEntity === entityId;

                      return (
                        <div key={entityId} className="border rounded-lg p-3 space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              {isEditing ? (
                                <Select
                                  value={currentType}
                                  onValueChange={(value) => handleEntityTypeChange(entityId, value)}
                                  disabled={isSaving || typesLoading}
                                >
                                  <SelectTrigger className="w-36">
                                    {isSaving ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <SelectValue />
                                    )}
                                  </SelectTrigger>
                                  <SelectContent className="dark">
                                    {entityTypes.map((t) => (
                                      <SelectItem key={t.name} value={t.name}>
                                        {t.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <>
                                  <Badge variant="outline" className={getTypeColor(currentType)}>
                                    {currentType}
                                  </Badge>
                                  {entity.manually_corrected && (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger>
                                          <CheckCircle2 className="h-3 w-3 text-emerald-400" />
                                        </TooltipTrigger>
                                        <TooltipContent className="dark">
                                          Previously: {entity.previous_type}
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                </>
                              )}
                            </div>
                            <Badge variant="outline" className={getConfidenceColor(entity.confidence || 0)}>
                              {((entity.confidence || 0) * 100).toFixed(0)}%
                            </Badge>
                          </div>
                          <div>
                            <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Value</span>
                            {isEditing ? (
                              <Input
                                defaultValue={currentValue}
                                className="w-full mt-1"
                                disabled={isSaving}
                                onBlur={(e) => {
                                  const newValue = e.target.value.trim();
                                  if (newValue && newValue !== currentValue) {
                                    handleEntityValueChange(entityId, newValue);
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    const newValue = (e.target as HTMLInputElement).value.trim();
                                    if (newValue && newValue !== currentValue) {
                                      handleEntityValueChange(entityId, newValue);
                                    }
                                  }
                                }}
                              />
                            ) : (
                              <p className="text-sm font-medium break-words">{currentValue}</p>
                            )}
                          </div>
                          {entity.context && (
                            <div>
                              <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Context</span>
                              <p className="text-xs text-muted-foreground break-words">
                                {entity.context}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="hidden sm:block border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-44">Type</TableHead>
                          <TableHead className="w-60">Value</TableHead>
                          <TableHead className="w-28">Confidence</TableHead>
                          <TableHead>Context</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {entities.map((entity) => {
                          const entityId = getEntityId(entity);
                          const currentType = getEntityType(entity);
                          const currentValue = getEntityValue(entity);
                          const isSaving = savingEntity === entityId;

                          return (
                            <TableRow key={entityId}>
                              <TableCell>
                                {isEditing ? (
                                  <Select
                                    value={currentType}
                                    onValueChange={(value) => handleEntityTypeChange(entityId, value)}
                                    disabled={isSaving || typesLoading}
                                  >
                                    <SelectTrigger className="w-40">
                                      {isSaving ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <SelectValue />
                                      )}
                                    </SelectTrigger>
                                    <SelectContent className="dark">
                                      {entityTypes.map((t) => (
                                        <SelectItem key={t.name} value={t.name}>
                                          {t.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline" className={getTypeColor(currentType)}>
                                      {currentType}
                                    </Badge>
                                    {entity.manually_corrected && (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger>
                                            <CheckCircle2 className="h-3 w-3 text-emerald-400" />
                                          </TooltipTrigger>
                                          <TooltipContent className="dark">
                                            Previously: {entity.previous_type}
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className="font-medium">
                                {isEditing ? (
                                  <Input
                                    defaultValue={currentValue}
                                    className="w-full"
                                    disabled={isSaving}
                                    onBlur={(e) => {
                                      const newValue = e.target.value.trim();
                                      if (newValue && newValue !== currentValue) {
                                        handleEntityValueChange(entityId, newValue);
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        const newValue = (e.target as HTMLInputElement).value.trim();
                                        if (newValue && newValue !== currentValue) {
                                          handleEntityValueChange(entityId, newValue);
                                        }
                                      }
                                    }}
                                  />
                                ) : (
                                  currentValue
                                )}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className={getConfidenceColor(entity.confidence || 0)}>
                                  {((entity.confidence || 0) * 100).toFixed(0)}%
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="text-xs text-muted-foreground max-w-48 truncate block">
                                        {entity.context || 'N/A'}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent className="dark max-w-md">
                                      {entity.context || 'N/A'}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No entities found in this document.</p>
                  </div>
                )}
              </TabsContent>

              {/* Bulk Find & Replace Tab */}
              <TabsContent value="bulk" className="space-y-4">
                <Card className="bg-blue-500/10 border-blue-500/30">
                  <CardContent className="py-4 flex items-start gap-3">
                    <Info className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Bulk Entity Type Correction</p>
                      <p className="text-sm text-blue-200">
                        Use this tool to find all entities with a specific value and type,
                        then change their type across ALL approved documents at once.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Find & Replace Entity Types</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex flex-wrap gap-4 items-end">
                      <div className="space-y-1">
                        <label className="text-sm text-muted-foreground">Entity Value</label>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="e.g., Irvine"
                            value={bulkSearchValue}
                            onChange={(e) => setBulkSearchValue(e.target.value)}
                            className="pl-9 w-[200px]"
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <label className="text-sm text-muted-foreground">Current Type</label>
                        <Select value={bulkFromType} onValueChange={setBulkFromType}>
                          <SelectTrigger className="w-[180px]">
                            <SelectValue placeholder="e.g., PERSON" />
                          </SelectTrigger>
                          <SelectContent className="dark">
                            {entityTypes.map((t) => (
                              <SelectItem key={t.name} value={t.name}>
                                <Badge variant="outline" className={getTypeColor(t.name)}>{t.label}</Badge>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="pb-2">
                        <ArrowRightLeft className="h-5 w-5 text-primary" />
                      </div>
                      <div className="space-y-1">
                        <label className="text-sm text-muted-foreground">New Type</label>
                        <Select value={bulkToType} onValueChange={setBulkToType}>
                          <SelectTrigger className="w-[180px]">
                            <SelectValue placeholder="e.g., LOCATION" />
                          </SelectTrigger>
                          <SelectContent className="dark">
                            {entityTypes.map((t) => (
                              <SelectItem key={t.name} value={t.name}>
                                <Badge variant="outline" className={getTypeColor(t.name)}>{t.label}</Badge>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        onClick={handleBulkPreview}
                        disabled={bulkLoading || !bulkSearchValue || !bulkFromType}
                      >
                        {bulkLoading ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Search className="h-4 w-4 mr-2" />
                        )}
                        Preview
                      </Button>
                    </div>

                    {/* Preview Results */}
                    {bulkPreview && (
                      <Card className={bulkPreview.matching_entities > 0
                        ? "bg-emerald-500/10 border-emerald-500/30"
                        : "bg-orange-500/10 border-orange-500/30"
                      }>
                        <CardContent className="py-4 space-y-4">
                          <div className="flex justify-between items-center">
                            <div>
                              <span className="text-lg font-semibold">
                                Found {bulkPreview.matching_entities} entities
                              </span>
                              <span className="text-muted-foreground ml-2">
                                in {bulkPreview.affected_documents.length} documents
                              </span>
                            </div>
                            {bulkPreview.matching_entities > 0 && bulkToType && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button disabled={bulkLoading}>
                                    {bulkLoading ? (
                                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                      <ArrowRightLeft className="h-4 w-4 mr-2" />
                                    )}
                                    Apply to All
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent className="dark bg-background text-foreground">
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Confirm Bulk Update</AlertDialogTitle>
                                    <AlertDialogDescription className="space-y-2">
                                      <p>
                                        This will change <strong>{bulkPreview.matching_entities}</strong> entities
                                        from <Badge variant="outline" className={getTypeColor(bulkFromType)}>{bulkFromType}</Badge> to{' '}
                                        <Badge variant="outline" className={getTypeColor(bulkToType)}>{bulkToType}</Badge>
                                      </p>
                                      <p className="flex items-center gap-2 text-amber-400">
                                        <AlertTriangle className="h-4 w-4" />
                                        This action cannot be easily undone.
                                      </p>
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={handleBulkUpdate}>
                                      Yes, Update All
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>

                          {bulkPreview.affected_documents.length > 0 && (
                            <div className="max-h-48 overflow-auto">
                              <p className="text-sm text-muted-foreground mb-2">Affected documents:</p>
                              <ul className="text-sm list-disc pl-5 space-y-1">
                                {bulkPreview.affected_documents.map((doc) => (
                                  <li key={doc.document_id}>
                                    {doc.file_name} ({doc.matching_entities} entities)
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {bulkPreview.matching_entities === 0 && (
                            <Card className="bg-amber-500/10 border-amber-500/30">
                              <CardContent className="py-3 flex items-center gap-2">
                                <AlertTriangle className="h-4 w-4 text-amber-400" />
                                <span className="text-sm text-amber-300">
                                  No entities with value "{bulkSearchValue}" and type "{bulkFromType}" were found.
                                </span>
                              </CardContent>
                            </Card>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </CardContent>
                </Card>

                {/* Common Misclassifications - Separate Card */}
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm">Common Misclassifications to Fix</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setBulkSearchValue('Irvine');
                          setBulkFromType('PERSON');
                          setBulkToType('LOCATION');
                          setBulkPreview(null);
                        }}
                      >
                        Irvine: PERSON → LOCATION
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setBulkSearchValue('Tustin');
                          setBulkFromType('PERSON');
                          setBulkToType('LOCATION');
                          setBulkPreview(null);
                        }}
                      >
                        Tustin: PERSON → LOCATION
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setBulkSearchValue('CA 92614');
                          setBulkFromType('DATE');
                          setBulkToType('LOCATION');
                          setBulkPreview(null);
                        }}
                      >
                        CA 92614: DATE → LOCATION
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Metadata Tab */}
              <TabsContent value="metadata">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Document Metadata</CardTitle>
                  </CardHeader>
                  <CardContent className="overflow-hidden">
                    {detail.extracted_metadata && Object.keys(detail.extracted_metadata).length > 0 ? (
                      <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto max-h-96 font-mono whitespace-pre-wrap break-words">
                        {JSON.stringify(parseNestedJson(detail.extracted_metadata), null, 2)}
                      </pre>
                    ) : (
                      <p className="text-muted-foreground">No additional metadata available</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Entity Relationships Tab */}
              <TabsContent value="entity-relationships" className="space-y-4">
                <EntityRelationshipsTab
                  documentId={documentId || ''}
                  stagingRelationships={detail.extracted_metadata?.ai_metadata?.relationships?.map((rel: any) => ({
                    relationship_id: `${rel.source_entity_value}-${rel.relationship_type}-${rel.target_value}`,
                    source_entity: rel.source_entity_value,
                    target_entity: rel.target_value,
                    relationship_type: rel.relationship_type,
                    confidence: rel.confidence_score,
                    context: rel.context_text,
                  }))}
                  isStaging={true}
                />
              </TabsContent>

              {/* Embeddings Tab */}
              {/* <TabsContent value="embeddings">
                <EmbeddingsTab
                  documentId={documentId || ''}
                  filename={detail.file_name}
                  isStaging={false}
                />
              </TabsContent> */}

              {/* Knowledge Graph Tab */}
              {/* <TabsContent value="graph">
                <KnowledgeGraphTab
                  documentId={documentId || ''}
                  filename={detail.file_name}
                  isStaging={false}
                />
              </TabsContent> */}
            </Tabs>
          </>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>Document not found.</p>
          </div>
        )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ApprovedDocumentModal;
